/**
 * Logout when the app browser-session ends (all tabs closed / browser closed).
 * Does NOT logout on single-tab close while another tab is open, or on normal refresh.
 *
 * - sessionStorage marker survives refresh (same tab).
 * - localStorage tab registry + heartbeat tracks live tabs.
 * - Closing last tab / browser: next cold start revokes token + clears auth.
 *   (Browsers cannot distinguish "browser closed" vs "last app tab closed".)
 */
import { BaseApiDomain } from "../env";

const HEARTBEAT_KEY = "auth_session_heartbeat";
const TABS_KEY = "auth_open_tabs";
const SESSION_FLAG = "auth_app_session";
const STALE_MS = 45000;

function clearLocalAuth() {
  localStorage.removeItem("userData");
  localStorage.removeItem("lastAccess");
  localStorage.removeItem("otp_token");
  localStorage.removeItem(HEARTBEAT_KEY);
  localStorage.removeItem(TABS_KEY);
}

function getTabs() {
  try {
    return JSON.parse(localStorage.getItem(TABS_KEY) || "{}");
  } catch (_) {
    return {};
  }
}

function pruneTabs(tabs) {
  const now = Date.now();
  const next = {};
  Object.entries(tabs || {}).forEach(([id, ts]) => {
    if (typeof ts === "number" && now - ts < STALE_MS * 2) {
      next[id] = ts;
    }
  });
  localStorage.setItem(TABS_KEY, JSON.stringify(next));
  return next;
}

function anyLiveTabs(tabs) {
  const now = Date.now();
  return Object.values(tabs || {}).some(
    (ts) => typeof ts === "number" && now - ts < STALE_MS
  );
}

function revokeTokenKeepalive() {
  try {
    const user = JSON.parse(localStorage.getItem("userData") || "null");
    if (!user) return;
    const panel =
      user.panel === "department"
        ? "department"
        : user.panel === "costcenter"
          ? "costcenter"
          : "staff";
    fetch(`${BaseApiDomain}${panel}/logout`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: "{}",
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    /* ignore */
  }
}

export function startBrowserSessionGuard() {
  // TEMP: disabled — was logging users out right after login (full reload
  // looked like a cold start because SESSION_FLAG is only set after ProtectedRoute).
  return () => {};

  if (typeof window === "undefined") return () => {};

  const hasAuth = !!localStorage.getItem("userData");
  let tabId = sessionStorage.getItem(SESSION_FLAG);
  const isSameTabRefresh = !!tabId;
  const tabs = pruneTabs(getTabs());
  const heartbeat = parseInt(localStorage.getItem(HEARTBEAT_KEY) || "0", 10);
  const heartbeatFresh = heartbeat > 0 && Date.now() - heartbeat < STALE_MS;

  // New browser/tab session after all app tabs died.
  if (hasAuth && !isSameTabRefresh && !anyLiveTabs(tabs) && !heartbeatFresh) {
    revokeTokenKeepalive();
    clearLocalAuth();
    if (
      window.location.pathname !== "/" &&
      !window.location.pathname.includes("login")
    ) {
      window.location.replace("/");
      return () => {};
    }
  }

  if (!tabId) {
    tabId =
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(SESSION_FLAG, tabId);
  }

  const touch = () => {
    if (!localStorage.getItem("userData")) return;
    const current = pruneTabs(getTabs());
    current[tabId] = Date.now();
    localStorage.setItem(TABS_KEY, JSON.stringify(current));
    localStorage.setItem(HEARTBEAT_KEY, String(Date.now()));
  };

  touch();

  const heartbeatTimer = setInterval(touch, 15000);

  const onPageHide = () => {
    const current = getTabs();
    delete current[tabId];
    const remaining = pruneTabs(current);
    if (Object.keys(remaining).length === 0) {
      // Last app tab leaving — may be refresh (sessionStorage survives) or real close.
      // Soft-stale heartbeat so a later cold start can detect session end without
      // revoking the token during a same-tab refresh.
      localStorage.setItem(
        HEARTBEAT_KEY,
        String(Date.now() - STALE_MS + 5000)
      );
    } else {
      const latest = Math.max(...Object.values(remaining));
      localStorage.setItem(HEARTBEAT_KEY, String(latest));
    }
  };

  const onPageShow = () => {
    touch();
  };

  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);

  return () => {
    clearInterval(heartbeatTimer);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
  };
}
